import type { StatKey } from './attributes.js';
import type { WeaponType } from './sects.js';

export const ITEM_CATEGORIES = [
  'weapon',
  'armor',
  'helmet',
  'boots',
  'belt',
  'ring',
  'necklace',
  'consumable',
  'material',
  'quest',
  'skill_book',
  'pet',
  'mount',
] as const;
export type ItemCategory = (typeof ITEM_CATEGORIES)[number];

export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'] as const;
export type Rarity = (typeof RARITIES)[number];

export const RARITY_INFO: Readonly<Record<Rarity, { name: string; color: string }>> = {
  common: { name: 'Common', color: '#d8d2c4' },
  uncommon: { name: 'Uncommon', color: '#6fd17a' },
  rare: { name: 'Rare', color: '#5fa8ff' },
  epic: { name: 'Epic', color: '#c27cff' },
  legendary: { name: 'Legendary', color: '#ffb547' },
  mythic: { name: 'Mythic', color: '#ff5a5a' },
};

/** Equipment slots. Only 'weapon' and 'chest' have items in the current milestone. */
export const EQUIP_SLOTS = ['weapon', 'chest', 'head', 'feet', 'waist', 'ring', 'neck'] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];

export type ItemUse = { kind: 'heal'; amount: number } | { kind: 'restore_mp'; amount: number };

export interface ItemDef {
  id: string;
  name: string;
  category: ItemCategory;
  rarity: Rarity;
  description: string;
  stackSize: number;
  equipSlot?: EquipSlot;
  weaponType?: WeaponType;
  levelRequired: number;
  /** Stat ranges rolled once when the item instance is created. */
  stats?: Partial<Record<StatKey, [number, number]>>;
  use?: ItemUse;
  /** Shared cooldown group for consumables. */
  cooldownGroup?: string;
  cooldownMs?: number;
  sellPrice: number;
  icon: string;
  /** Client model key for equipped weapons / robes. */
  model?: string;
  tint?: string;
}

export const ITEMS: Readonly<Record<string, ItemDef>> = {
  wooden_practice_sword: {
    id: 'wooden_practice_sword',
    name: 'Wooden Practice Sword',
    category: 'weapon',
    rarity: 'common',
    description: 'Peach-wood training blade issued to every new disciple.',
    stackSize: 1,
    equipSlot: 'weapon',
    weaponType: 'sword',
    levelRequired: 1,
    stats: { atk: [3, 3] },
    sellPrice: 1,
    icon: 'sword',
    model: 'sword_wood',
    tint: '#a9774a',
  },
  worn_iron_sword: {
    id: 'worn_iron_sword',
    name: 'Worn Iron Sword',
    category: 'weapon',
    rarity: 'common',
    description: 'Pitted but serviceable. Someone lost this in the meadow grass.',
    stackSize: 1,
    equipSlot: 'weapon',
    weaponType: 'sword',
    levelRequired: 1,
    stats: { atk: [5, 7] },
    sellPrice: 6,
    icon: 'sword',
    model: 'sword_iron',
    tint: '#9aa1a8',
  },
  fine_steel_sword: {
    id: 'fine_steel_sword',
    name: 'Fine Steel Jian',
    category: 'weapon',
    rarity: 'uncommon',
    description: 'A well-balanced straight sword taken from a bandit lieutenant.',
    stackSize: 1,
    equipSlot: 'weapon',
    weaponType: 'sword',
    levelRequired: 2,
    stats: { atk: [9, 11], crit: [0.01, 0.02] },
    sellPrice: 15,
    icon: 'sword',
    model: 'sword_steel',
    tint: '#d7dde3',
  },
  pine_needle_sword: {
    id: 'pine_needle_sword',
    name: 'Pine-Needle Sword',
    category: 'weapon',
    rarity: 'rare',
    description: 'Elder Bai’s old travelling sword. Its edge hums faintly with green qi.',
    stackSize: 1,
    equipSlot: 'weapon',
    weaponType: 'sword',
    levelRequired: 2,
    stats: { atk: [12, 12], qiAtk: [5, 5], crit: [0.03, 0.03] },
    sellPrice: 25,
    icon: 'sword',
    model: 'sword_jade',
    tint: '#7fd1a0',
  },
  hempen_robe: {
    id: 'hempen_robe',
    name: 'Hempen Disciple Robe',
    category: 'armor',
    rarity: 'common',
    description: 'Plain robe of undyed hemp.',
    stackSize: 1,
    equipSlot: 'chest',
    levelRequired: 1,
    stats: { def: [2, 2] },
    sellPrice: 1,
    icon: 'robe',
    tint: '#cfc6b0',
  },
  wolfhide_jerkin: {
    id: 'wolfhide_jerkin',
    name: 'Wolfhide Jerkin',
    category: 'armor',
    rarity: 'common',
    description: 'Rough grey hide stitched over quilted cloth.',
    stackSize: 1,
    equipSlot: 'chest',
    levelRequired: 1,
    stats: { def: [4, 5], hp: [10, 16] },
    sellPrice: 5,
    icon: 'robe',
    tint: '#8b8681',
  },
  bandit_leather_vest: {
    id: 'bandit_leather_vest',
    name: 'Bandit Leather Vest',
    category: 'armor',
    rarity: 'uncommon',
    description: 'Boiled leather plates, still smelling of campfire smoke.',
    stackSize: 1,
    equipSlot: 'chest',
    levelRequired: 2,
    stats: { def: [6, 8], hp: [20, 30] },
    sellPrice: 12,
    icon: 'robe',
    tint: '#7a5236',
  },
  cloud_silk_robe: {
    id: 'cloud_silk_robe',
    name: 'Cloud Silk Robe',
    category: 'armor',
    rarity: 'rare',
    description: 'Woven by the village weavers from silk said to be spun beneath the clouds of Wudang.',
    stackSize: 1,
    equipSlot: 'chest',
    levelRequired: 3,
    stats: { def: [8, 8], hp: [25, 25], mp: [30, 30] },
    sellPrice: 30,
    icon: 'robe',
    tint: '#cfe0ff',
  },
  minor_healing_pill: {
    id: 'minor_healing_pill',
    name: 'Minor Healing Pill',
    category: 'consumable',
    rarity: 'common',
    description: 'Restores 90 health. Shares a cooldown with other pills.',
    stackSize: 20,
    levelRequired: 1,
    use: { kind: 'heal', amount: 90 },
    cooldownGroup: 'pill',
    cooldownMs: 8000,
    sellPrice: 2,
    icon: 'pill_red',
  },
  minor_qi_pill: {
    id: 'minor_qi_pill',
    name: 'Minor Qi Pill',
    category: 'consumable',
    rarity: 'common',
    description: 'Restores 60 qi. Shares a cooldown with other pills.',
    stackSize: 20,
    levelRequired: 1,
    use: { kind: 'restore_mp', amount: 60 },
    cooldownGroup: 'pill',
    cooldownMs: 8000,
    sellPrice: 3,
    icon: 'pill_blue',
  },
  wolf_pelt: {
    id: 'wolf_pelt',
    name: 'Wolf Pelt',
    category: 'material',
    rarity: 'common',
    description: 'Coarse grey fur. The herbalist buys these for the winter trade.',
    stackSize: 50,
    levelRequired: 1,
    sellPrice: 3,
    icon: 'pelt',
  },
  wolf_fang: {
    id: 'wolf_fang',
    name: 'Wolf Fang',
    category: 'material',
    rarity: 'uncommon',
    description: 'A curved fang, prized for carving talismans.',
    stackSize: 50,
    levelRequired: 1,
    sellPrice: 6,
    icon: 'fang',
  },
  stolen_rice_sack: {
    id: 'stolen_rice_sack',
    name: 'Stolen Rice Sack',
    category: 'quest',
    rarity: 'common',
    description: 'Willow Creek’s autumn rice, stamped with the village granary seal.',
    stackSize: 10,
    levelRequired: 1,
    sellPrice: 0,
    icon: 'sack',
  },
};

export function getItem(id: string): ItemDef | undefined {
  return ITEMS[id];
}

export function isEquippable(def: ItemDef): boolean {
  return def.equipSlot !== undefined;
}

export interface LootEntry {
  itemId: string;
  chance: number;
  min: number;
  max: number;
  /** Only drops for players who have this quest active. */
  requiresQuest?: string;
}

export interface LootTable {
  id: string;
  coins: [number, number];
  entries: LootEntry[];
}

export const LOOT_TABLES: Readonly<Record<string, LootTable>> = {
  grey_wolf: {
    id: 'grey_wolf',
    coins: [1, 4],
    entries: [
      { itemId: 'wolf_pelt', chance: 0.55, min: 1, max: 1 },
      { itemId: 'wolf_fang', chance: 0.18, min: 1, max: 1 },
      { itemId: 'worn_iron_sword', chance: 0.1, min: 1, max: 1 },
      { itemId: 'wolfhide_jerkin', chance: 0.07, min: 1, max: 1 },
      { itemId: 'minor_healing_pill', chance: 0.1, min: 1, max: 1 },
    ],
  },
  mountain_bandit: {
    id: 'mountain_bandit',
    coins: [6, 14],
    entries: [
      { itemId: 'stolen_rice_sack', chance: 0.7, min: 1, max: 1, requiresQuest: 'q_ashes_eastern_road' },
      { itemId: 'bandit_leather_vest', chance: 0.14, min: 1, max: 1 },
      { itemId: 'fine_steel_sword', chance: 0.1, min: 1, max: 1 },
      { itemId: 'minor_qi_pill', chance: 0.15, min: 1, max: 1 },
      { itemId: 'minor_healing_pill', chance: 0.2, min: 1, max: 2 },
    ],
  },
};
